





















import Vue from 'vue'
import { Component, Prop, Emit, Watch } from 'vue-property-decorator'
import { showErrors, showNotification } from '@/utils/snack'
import { SnackType } from '@/interfaces/snack'
import AppDialog from '@/components/AppDialog.vue'
import AppSteps from '@/components/AppSteps.vue'
import AgencyFormInfo from '@/modules/Agency/components/forms/AgencyFormInfo.vue'
import { newAgency, editAgency } from '@/modules/Agency/gql/agency.gql'
import { IAgencyInfoInput } from '@/modules/Agency/interfaces/agency-form'
import { Agency } from '@/modules/Agency/interfaces/agency'
import { states } from '@/utils/us_states'
import delNonNumCode from '@/utils/delNonNumCode'

@Component({
  components: {
    AgencyFormInfo,
    AppDialog,
    AppSteps
  }
})
export default class AgencyForm extends Vue {
  @Prop(Boolean) value!: boolean
  @Prop(Number) readonly step!: number
  @Prop(String) readonly title!: string
  @Prop({ type: Object as () => Agency }) readonly agency!: Agency

  showDialog = false

  loading = false

  stepIndex = 0
  steps = [
    ['Information', 770, 511]
  ]

  infoInput: IAgencyInfoInput = {
    name: '',
    phone: '',
    fax: '',
    mailingStreet: '',
    mailingCity: '',
    mailingStateIdx: -1,
    mailingZipCode: '',
    mailingCountry: ''
  }

  get stepItems () {
    return this.steps.map(st => st[0])
  }

  get showBackBtn () {
    return this.stepIndex > 0
  }

  get confirmText () {
    return this.stepIndex < this.steps.length - 1
      ? 'Next'
      : this.agency
        ? 'Save'
        : 'Create'
  }

  @Watch('value')
  onValueChanged (val: boolean) {
    if (val) {
      this.resetStep()
      this.initInput()
    }
    this.showDialog = val
  }

  resetStep () {
    this.stepIndex = this.step
  }

  initInput () {
    if (this.agency) {
      const {
        name,
        phone, countryCode, extensionNumber, countryCallingCode,
        fax, faxCountryCode, faxCountryCallingCode, faxExtensionNumber,
        mailingStreet, mailingCity, mailingState, mailingZipCode, mailingCountry
      } = this.agency

      this.infoInput = {
        name,
        phone: (countryCode || '') + ',' + (phone || '') + ',' + (extensionNumber || '') + ',' + (countryCallingCode || ''),
        fax: (faxCountryCode || '') + ',' + (fax || '') + ',' + (faxExtensionNumber || '') + ',' + (faxCountryCallingCode || ''),
        mailingStreet,
        mailingCity,
        mailingStateIdx: states.findIndex(key => key === mailingState),
        mailingZipCode,
        mailingCountry
      }
    } else {
      this.clearForm()
    }
  }

  handleStepClick (index: number) {
    if (!this.agency) {
      return
    }

    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    this.stepIndex = index
  }

  handleValidate () {
    const { stepIndex, steps } = this
    const refName = steps[stepIndex][0] as string
    return (this.$refs[refName] as Vue & {
      validate: () => null | { [key: string]: number | string }
    }).validate()
  }

  handleConfirm (event: Event, finishAhead = false) {
    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    if (this.stepIndex === this.steps.length - 1 || finishAhead) {
      this.loading = true
      if (this.agency) {
        this.handleUpdate()
      } else {
        this.handleCreate()
      }
    } else {
      this.stepIndex += 1
    }
  }

  handleBack () {
    if (this.stepIndex > 0) {
      this.stepIndex -= 1
    }
  }

  async handleCreate () {
    const {
      infoInput: {
        name,
        phone, fax, mailingStreet, mailingCity, mailingStateIdx, mailingZipCode, mailingCountry
      }
    } = this

    const phoneArr = phone.split(',')
    const faxArr = fax.split(',')

    try {
      const res = await this.$apollo.mutate({
        mutation: newAgency,
        variables: {
          agencyInput: {
            name,
            fax: delNonNumCode(faxArr[1] || ''),
            phone: delNonNumCode(phoneArr[1] || ''),
            mailingStreet,
            mailingCity,
            mailingState: states[mailingStateIdx] || null,
            mailingZipCode,
            mailingCountry,
            extensionNumber: phoneArr[2] || '',
            countryCode: phoneArr[0] || '',
            countryCallingCode: phoneArr[3] || '',
            faxExtensionNumber: faxArr[2] || '',
            faxCountryCode: faxArr[0] || '',
            faxCountryCallingCode: faxArr[3] || ''
          }
        }
      })
      this.clearForm()
      this.onSuccess('Create the new source successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  async handleUpdate () {
    const {
      infoInput: {
        name,
        phone, fax, mailingStreet, mailingCity, mailingStateIdx, mailingZipCode, mailingCountry
      }
    } = this

    const phoneArr = phone.split(',')
    const faxArr = fax.split(',')

    try {
      const res = await this.$apollo.mutate({
        mutation: editAgency,
        variables: {
          agencyId: +this.agency.id,
          agencyInput: {
            name,
            fax: delNonNumCode(faxArr[1] || ''),
            phone: delNonNumCode(phoneArr[1] || ''),
            mailingStreet,
            mailingCity,
            mailingState: states[mailingStateIdx] || null,
            mailingZipCode,
            mailingCountry,
            extensionNumber: phoneArr[2] || '',
            countryCode: phoneArr[0] || '',
            countryCallingCode: phoneArr[3] || '',
            faxExtensionNumber: faxArr[2] || '',
            faxCountryCode: faxArr[0] || '',
            faxCountryCallingCode: faxArr[3] || ''
          }
        }
      })
      this.clearForm()
      this.onSuccess('Update agency successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  clearForm () {
    this.loading = false
    this.infoInput = {
      name: '',
      phone: '',
      fax: '',
      mailingStreet: '',
      mailingCity: '',
      mailingStateIdx: -1,
      mailingZipCode: '',
      mailingCountry: ''
    }
  }

  @Emit('hide')
  handleHide () {
    this.$emit('input', false)
  }

  @Emit('cancel')
  handleCancel () {
    this.handleHide()
    this.clearForm()
  }

  @Emit('success')
  @Emit('hide')
  onSuccess (msg = '') {
    this.handleHide()
    this.resetStep()
    if (msg) {
      showNotification({
        msg,
        type: SnackType.Success
      })
    }
  }
}
